import { useTranslate } from "@refinedev/core";
import { Form } from "antd";
import { useEffect } from "react";
import styled from "styled-components";
import { isValidShareEmail, radius } from "@libertify/ui";
import { usePublishContext } from "./PublishContext";
import { VideoShareEmailConfigComponent } from "./VideoShareEmailConfigComponent";
import {
  FormValues,
  VideoSharePasswordConfigComponent,
} from "./VideoSharePasswordConfigComponent";

const EXPERIENCE_TYPES = {
  PUBLIC_LINK: "publicLink",
  PIN_VIA_EMAIL: "pinViaEmail",
  PASSWORD_PROTECTED: "passwordProtected",
} as const;

type ExperienceType = (typeof EXPERIENCE_TYPES)[keyof typeof EXPERIENCE_TYPES];

type ExperienceOptionType = {
  labelKey: string;
  content: string;
  value: ExperienceType;
};

export const ProjectExperienceSelection = () => {
  const t = useTranslate();
  const {
    projectExperienceType,
    setProjectExperienceType,
    setIsEmailValid,
    setIsPasswordValid,
    setPassword,
    setEmails,
  } = usePublishContext();

  const [emailForm] = Form.useForm();
  const [passwordForm] = Form.useForm<FormValues>();

  const passwordValue = Form.useWatch("password", passwordForm);
  const confirmPasswordValue = Form.useWatch("confirmPassword", passwordForm);
  const emailsValue = Form.useWatch("emails", emailForm);

  const experienceSelected =
    projectExperienceType || EXPERIENCE_TYPES.PUBLIC_LINK;
  const setExperienceSelected = (value: ExperienceType) => {
    setProjectExperienceType(value);
  };

  // parse and validate emails
  useEffect(() => {
    const emailsText = emailsValue || "";
    const parsedEmails = emailsText
      .split(/[,;\n\r]+/)
      .map((e: string) => e.trim())
      .filter((e: string) => isValidShareEmail(e));

    setIsEmailValid(parsedEmails.length > 0);
    setEmails(parsedEmails);
  }, [emailsValue, setIsEmailValid, setEmails, experienceSelected]);

  // validate password and update context
  useEffect(() => {
    const isPasswordValid = passwordValue && passwordValue.length >= 8;
    const doPasswordsMatch = passwordValue === confirmPasswordValue;
    const isValid =
      isPasswordValid && doPasswordsMatch && !!confirmPasswordValue;

    setIsPasswordValid(isValid);

    if (isPasswordValid && doPasswordsMatch && passwordValue) {
      setPassword(passwordValue);
    } else {
      setPassword(null);
    }
  }, [
    passwordValue,
    confirmPasswordValue,
    setIsPasswordValid,
    setPassword,
    experienceSelected,
  ]);

  const experienceOptions: ExperienceOptionType[] = [
    {
      labelKey: "projects.components.VideoShareAccess.publicLink",
      content: "projects.components.VideoShareAccess.anyoneWithTheLinkCanView",
      value: EXPERIENCE_TYPES.PUBLIC_LINK,
    },
    {
      labelKey: "projects.components.VideoShareAccess.pinViaEmail",
      content: "projects.components.VideoShareAccess.pinViaEmailSubtitle",
      value: EXPERIENCE_TYPES.PIN_VIA_EMAIL,
    },
    {
      labelKey: "projects.components.VideoShareAccess.passwordProtected",
      content: "projects.components.VideoShareAccess.passwordProtectedSubtitle",
      value: EXPERIENCE_TYPES.PASSWORD_PROTECTED,
    },
  ];

  const defaultExperienceOption = experienceOptions[0];
  const advancedExperienceOptions = experienceOptions.slice(1);

  return (
    <RadioGroupSM>
      <RadioOptionSM
        $selected={defaultExperienceOption.value === experienceSelected}
        onClick={() => setExperienceSelected(defaultExperienceOption.value)}
      >
        <input
          type="radio"
          readOnly
          checked={defaultExperienceOption.value === experienceSelected}
          aria-label={t(defaultExperienceOption.labelKey)}
        />
        <RadioDotSM
          $selected={defaultExperienceOption.value === experienceSelected}
        />
        <RadioLabelSM>
          <RadioTitleSM>{t(defaultExperienceOption.labelKey)}</RadioTitleSM>
          <RadioDescSM>{t(defaultExperienceOption.content)}</RadioDescSM>
        </RadioLabelSM>
      </RadioOptionSM>

      <RestrictedCardSM>
        <RestrictedLabelSM>
          {t("projects.components.VideoShareAccess.restrictedAccess")}
        </RestrictedLabelSM>
        <RestrictedOptionsSM>
          {advancedExperienceOptions.map((option) => {
            const isChecked = option.value === experienceSelected;

            return (
              <RestrictedOptionSM
                key={option.value}
                $selected={isChecked}
                onClick={() => {
                  if (!isChecked) setExperienceSelected(option.value);
                }}
              >
                <input
                  type="radio"
                  readOnly
                  checked={isChecked}
                  aria-label={t(option.labelKey)}
                />
                <RadioDotSM $selected={isChecked} />
                <RadioLabelSM>
                  <RadioTitleSM>{t(option.labelKey)}</RadioTitleSM>
                  <RadioDescSM>{t(option.content)}</RadioDescSM>

                  {option.value === EXPERIENCE_TYPES.PASSWORD_PROTECTED && (
                    <CollapsibleContent $expanded={isChecked}>
                      <CollapsibleInner>
                        <VideoSharePasswordConfigComponent
                          form={passwordForm}
                          onValidationChange={setIsPasswordValid}
                        />
                      </CollapsibleInner>
                    </CollapsibleContent>
                  )}

                  {option.value === EXPERIENCE_TYPES.PIN_VIA_EMAIL && (
                    <CollapsibleContent $expanded={isChecked}>
                      <CollapsibleInner>
                        <VideoShareEmailConfigComponent form={emailForm} />
                      </CollapsibleInner>
                    </CollapsibleContent>
                  )}
                </RadioLabelSM>
              </RestrictedOptionSM>
            );
          })}
        </RestrictedOptionsSM>
      </RestrictedCardSM>
    </RadioGroupSM>
  );
};

const RadioGroupSM = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const RadioOptionSM = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1.5px solid ${(p) => (p.$selected ? "#14b8a6" : "#e5e7eb")};
  border-radius: ${radius.box}px;
  background: ${(p) => (p.$selected ? "#f0fdfb" : "white")};
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }
`;

const RadioDotSM = styled.span<{ $selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: ${radius.circle};
  border: 2px solid ${(p) => (p.$selected ? "#14b8a6" : "#d1d5db")};
  background: ${(p) => (p.$selected ? "#14b8a6" : "white")};
  flex-shrink: 0;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.15s,
    background 0.15s;

  &::after {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: ${radius.circle};
    background: white;
  }
`;

const RadioLabelSM = styled.div`
  flex: 1;
`;

const FONT_INTER = '"Inter Variable", "Inter", sans-serif';

const RadioTitleSM = styled.div`
  font-family: ${FONT_INTER};
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const RadioDescSM = styled.div`
  font-family: ${FONT_INTER};
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
  line-height: 1.5;
`;

const CollapsibleContent = styled.div<{ $expanded: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$expanded ? "1fr" : "0fr")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  transition:
    grid-template-rows 0.25s ease,
    opacity 0.2s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CollapsibleInner = styled.div`
  overflow: hidden;
  padding-top: 12px;
`;

const RestrictedCardSM = styled.div`
  border: 1.5px solid #e5e7eb;
  border-radius: ${radius.box}px;
  overflow: hidden;
`;

const RestrictedLabelSM = styled.div`
  padding: 6px 12px;
  background: #f9fafb;
  font-family: ${FONT_INTER};
  font-size: 11px;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #e5e7eb;
`;

const RestrictedOptionsSM = styled.div`
  display: flex;
  flex-direction: column;
`;

const RestrictedOptionSM = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? "#f0fdfb" : "white")};
  transition: background 0.15s;

  & + & {
    border-top: 1px solid #f3f4f6;
  }

  input[type="radio"] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }
`;
